import { assert } from '@takeshape/util';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { compatSchemaToSchema } from '../../../compat/utils.js';
import { BranchMergeOperation, closeBranchMerge, mergeBranchRoutine } from '../../actions.js';
import type { DevelopmentBranch, ProductionBranch } from '../../api.js';
import { useActiveSchema } from '../../hooks/use-active-schema.js';
import { getBranchByName, getBranchMergeState } from '../../selectors.js';
import type { BranchesSlice } from '../../types.js';
import { BRANCH_MERGE_TAB_DIFF, type BranchMergeHookProps, type BranchMergeTab } from './branch-merge.js';

export const useBranchMerge = (): BranchMergeHookProps => {
  const dispatch = useDispatch();
  const { dirty } = useActiveSchema();
  const [tabValue, setTabValue] = useState<BranchMergeTab>(BRANCH_MERGE_TAB_DIFF);

  const { open, loading, error, inProgress, mergePreview, branchName, operation } = useSelector(getBranchMergeState);

  const branch = useSelector((state: BranchesSlice) => getBranchByName(state, branchName ?? ''));
  const branchSchemaCompat = branch?.layers.user.data ?? null;
  const branchSchema = branchSchemaCompat ? compatSchemaToSchema(branchSchemaCompat) : null;

  const prod = useSelector((state: BranchesSlice) => getBranchByName(state, 'production'));
  const prodSchemaCompat = prod?.layers.user.data ?? null;
  const prodSchema = prodSchemaCompat ? compatSchemaToSchema(prodSchemaCompat) : null;

  const beforeSchema = operation === BranchMergeOperation.Update ? branchSchema : prodSchema;

  const handleClose = useCallback(() => {
    dispatch(closeBranchMerge());
  }, [dispatch]);

  const handleMerge = useCallback(() => {
    assert(branchName, 'no branch name');
    assert(prodSchemaCompat, 'no prod schema');
    assert(branchSchemaCompat, 'no branch schema');

    const base: ProductionBranch = {
      environment: 'PRODUCTION'
    };

    const head: DevelopmentBranch = {
      environment: 'DEVELOPMENT',
      branchName
    };

    const target =
      operation === BranchMergeOperation.Update
        ? {
            ...head,
            version: branchSchemaCompat.version
          }
        : {
            ...base,
            version: prodSchemaCompat.version
          };

    dispatch(
      mergeBranchRoutine.requested(undefined, {
        base,
        head,
        target,
        deleteMergedHead: operation === BranchMergeOperation.Promote
      })
    );
  }, [branchName, branchSchemaCompat, dispatch, operation, prodSchemaCompat]);

  const handleTabChange = useCallback((_event: React.SyntheticEvent, value: BranchMergeTab) => {
    setTabValue(value);
  }, []);

  return {
    open,
    loading,
    error,
    inProgress,
    tabValue,
    handleTabChange,
    handleMerge,
    mergePreview,
    beforeSchema,
    dirty,
    handleClose,
    operation,
    branchName
  };
};
